import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { CategoryGroupFormUtils } from '../../utils/voucherCategoryGroupCommon';
import { getCategoryGroupDetail, getVoucherCountByTreeCodes, CategoryGroupDetail, CategoryGroupTreeNode as ApiCategoryGroupTreeNode, getTreeVouchers, updateVoucherInTree } from '../../utils/voucherCategoryGroup';
import { getVoucherList, Voucher, VoucherListRequest } from '../../utils/voucher';
import { generateAfter, generateBetween } from '../../utils/fractionalIndex';
import { 
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  X,
  Package,
  FolderTree,
  GripVertical
} from 'lucide-react';


// 카테고리/그룹 트리 노드 인터페이스 (UI용)
interface CategoryGroupNode {
  code: string;
  title: string;
  brandCode?: string;
  parentCode?: string;
  depth: number;
  sortOrder?: number;
  status?: string;
  type?: string;
  children?: CategoryGroupNode[];
}

// API 트리 노드(sub) -> UI 트리 노드(children) 변환
const convertApiTreeToNodes = (nodes: ApiCategoryGroupTreeNode[] | undefined): CategoryGroupNode[] => {
  if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return [];
  return nodes.map((node, index) => ({
    code: node.code,
    title: node.title,
    depth: node.depth ?? 0,
    status: node.status,
    sortOrder: index,
    children: node.sub && node.sub.length > 0 ? convertApiTreeToNodes(node.sub) : undefined
  }));
};

// 카테고리/그룹 교환권 매핑 (로컬 상태, sortOrder는 fractional index)
interface CategoryGroupVoucher {
  categoryGroupCode: string;
  voucherCode: string;
  order: number;
  sortOrder: string;
}

const CategoryGroupVoucherConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryGroupCode } = useParams<{ categoryGroupCode: string }>();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('voucher-category-group-list');

  // 카테고리/그룹 트리 상태
  const [treeData, setTreeData] = useState<CategoryGroupNode[]>([]);
  const [selectedCategoryGroupCode, setSelectedCategoryGroupCode] = useState<string | null>(null);
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [treeNodeVoucherCounts, setTreeNodeVoucherCounts] = useState<Record<string, number>>({});
  const [treeCountsLoaded, setTreeCountsLoaded] = useState(false);

  // 교환권 상태
  const [allVouchers, setAllVouchers] = useState<Voucher[]>([]);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState(false);
  const [voucherListError, setVoucherListError] = useState<string | null>(null);

  // 드래그앤드랍 상태
  const [draggedVoucher, setDraggedVoucher] = useState<Voucher | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);
  const [dropInsertIndex, setDropInsertIndex] = useState<number | null>(null);

  // 카테고리/그룹별 교환권 목록 (노드 선택 시 API 로드, 드롭·삭제 시 즉시 API 호출로 반영)
  const [categoryGroupVouchers, setCategoryGroupVouchers] = useState<Map<string, CategoryGroupVoucher[]>>(new Map());
  const [isLoadingNodeVouchers, setIsLoadingNodeVouchers] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState<CategoryGroupDetail | null>(null);

  // Check if screen is mobile size
  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 카테고리/그룹 상세 정보 및 트리 데이터 로드 (한 번의 API 호출로 통합)
  useEffect(() => {
    const fetchCategoryGroupData = async () => {
      if (!projectDetail || !categoryGroupCode) {
        return;
      }

      try {
        setIsLoadingTree(true);
        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          return;
        }

        const categoryGroupDetail = await getCategoryGroupDetail(projectCode, categoryGroupCode);
        setSelectedCategoryGroup(categoryGroupDetail);

        // tree 필드에서 트리 노드 변환 (API sub -> UI children)
        const initialTreeData = convertApiTreeToNodes(categoryGroupDetail.tree);
        setTreeData(initialTreeData);

        // 모든 노드 펼치기
        const collectAllCodes = (nodes: CategoryGroupNode[]): string[] => {
          const codes: string[] = [];
          nodes.forEach(node => {
            codes.push(node.code);
            if (node.children && node.children.length > 0) {
              codes.push(...collectAllCodes(node.children));
            }
          });
          return codes;
        };
        setExpandedCodes(new Set(collectAllCodes(initialTreeData)));

        setTreeCountsLoaded(false);
        const nodeCodes = collectAllCodes(initialTreeData);
        if (nodeCodes.length > 0) {
          const treeCodes = nodeCodes.map((code) => `${categoryGroupCode}:${code}`);
          try {
            const counts = await getVoucherCountByTreeCodes(projectCode, treeCodes);
            setTreeNodeVoucherCounts(counts);
          } catch (countErr) {
            console.error('노드별 교환권 수 조회 오류:', countErr);
            setTreeNodeVoucherCounts({});
          }
        } else {
          setTreeNodeVoucherCounts({});
        }
        setTreeCountsLoaded(true);
      } catch (err) {
        console.error('카테고리/그룹 정보 조회 오류:', err);
        setSelectedCategoryGroup({
          code: categoryGroupCode,
          title: categoryGroupCode,
          status: 'ACTIVE',
          type: 'CATEGORY'
        } as CategoryGroupDetail);
        setTreeData([]);
        setTreeNodeVoucherCounts({});
      } finally {
        setTreeCountsLoaded(true);
        setIsLoadingTree(false);
      }
    };

    if (projectDetail) {
      fetchCategoryGroupData();
    }
  }, [projectDetail, categoryGroupCode]);

  // 전체 교환권 목록 조회 (현재 카테고리/그룹 상세 로드 후, 해당 브랜드로 필터)
  useEffect(() => {
    const fetchVouchers = async () => {
      if (!projectDetail || !categoryGroupCode || selectedCategoryGroup == null) {
        return;
      }

      try {
        setIsLoadingVouchers(true);
        setVoucherListError(null);

        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }

        const request: VoucherListRequest = {
          page: 0,
          size: 1000,
          ...(selectedCategoryGroup.brandCode && { brandCode: selectedCategoryGroup.brandCode })
        };

        const response = await getVoucherList(projectCode, request);
        setAllVouchers(response.vouchers || []);
      } catch (err) {
        console.error('교환권 목록 조회 오류:', err);
        setVoucherListError(err instanceof Error ? err.message : '교환권 목록을 불러오는 중 오류가 발생했습니다.');
        setAllVouchers([]);
      } finally {
        setIsLoadingVouchers(false);
      }
    };

    fetchVouchers();
  }, [projectDetail, categoryGroupCode, selectedCategoryGroup]);

  // 선택한 노드의 교환권 목록 조회 (tree-vouchers API)
  useEffect(() => {
    const fetchNodeVouchers = async () => {
      if (!projectDetail || !categoryGroupCode || !selectedCategoryGroupCode) {
        return;
      }

      const projectCode = projectDetail.code || getProjectCode();
      if (!projectCode) return;

      const treeCode = `${categoryGroupCode}:${selectedCategoryGroupCode}`;

      try {
        setIsLoadingNodeVouchers(true);
        const res = await getTreeVouchers(projectCode, [treeCode]);
        console.log('[tree-vouchers] 요청 treeCode:', treeCode, '응답:', res);
        const list: CategoryGroupVoucher[] = (res.vouchers || []).map((item, index) => ({
          categoryGroupCode: selectedCategoryGroupCode,
          voucherCode: item.voucherCode,
          order: index,
          sortOrder: item.sortOrder ?? ''
        }));
        setCategoryGroupVouchers(prev => new Map(prev).set(selectedCategoryGroupCode, list));
      } catch (err) {
        console.error('노드 교환권 목록 조회 오류:', err);
        setCategoryGroupVouchers(prev => new Map(prev).set(selectedCategoryGroupCode, []));
      } finally {
        setIsLoadingNodeVouchers(false);
      }
    };

    fetchNodeVouchers();
  }, [projectDetail, categoryGroupCode, selectedCategoryGroupCode]);

  // 카테고리/그룹 토글
  const handleCategoryGroupToggle = (code: string) => {
    setExpandedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  // 카테고리/그룹 선택
  const handleCategoryGroupSelect = (code: string) => {
    setSelectedCategoryGroupCode(code);
  };

  // 드래그 시작 (오른쪽에서)
  const handleDragStartFromAll = (voucher: Voucher) => (e: React.DragEvent) => {
    setDraggedVoucher(voucher);
    setDraggedIndex(null);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // 드래그 시작 (가운데에서 - 순서 변경용)
  const handleDragStartFromCategory = (voucher: Voucher, index: number) => (e: React.DragEvent) => {
    setDraggedVoucher(voucher);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // 드래그 종료
  const handleDragEnd = () => {
    setDraggedVoucher(null);
    setDraggedIndex(null);
    setDropZoneActive(false);
    setDropInsertIndex(null);
  };

  // treeCode 생성: 그룹코드:노드코드
  const getTreeCode = (): string | null => {
    if (!selectedCategoryGroupCode || !categoryGroupCode) return null;
    return `${categoryGroupCode}:${selectedCategoryGroupCode}`;
  };

  // 오른쪽 → 가운데: 지정한 인덱스(슬롭)에 교환권 추가
  const handleDropFromRight = async (insertIndex: number, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    setDropInsertIndex(null);

    if (!selectedCategoryGroupCode) {
      alert('카테고리/그룹을 먼저 선택해 주세요.');
      return;
    }
    if (!draggedVoucher || draggedIndex !== null || !projectDetail) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;

    const currentVouchers = categoryGroupVouchers.get(selectedCategoryGroupCode) || [];
    const treeCode = getTreeCode();
    if (!treeCode) return;

    if (currentVouchers.some(cv => cv.voucherCode === draggedVoucher.code)) {
      alert('이미 추가된 교환권입니다.');
      return;
    }

    const prev = insertIndex > 0 ? currentVouchers[insertIndex - 1] : null;
    const next = insertIndex < currentVouchers.length ? currentVouchers[insertIndex] : null;
    const sortOrder = generateBetween(prev?.sortOrder ?? null, next?.sortOrder ?? null);

    try {
      setApiBusy(true);
      await updateVoucherInTree(projectCode, draggedVoucher.code, {
        operation: 'update',
        treeCode,
        sortOrder
      });
      const newEntry: CategoryGroupVoucher = {
        categoryGroupCode: selectedCategoryGroupCode,
        voucherCode: draggedVoucher.code,
        order: insertIndex,
        sortOrder
      };
      const newVouchers = [...currentVouchers];
      newVouchers.splice(insertIndex, 0, newEntry);
      const withOrder = newVouchers.map((cv, index) => ({ ...cv, order: index }));
      setCategoryGroupVouchers(prev => new Map(prev).set(selectedCategoryGroupCode, withOrder));
    } catch (err) {
      console.error('교환권 추가 실패:', err);
      alert(err instanceof Error ? err.message : '교환권 추가에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  // 빈 가운데 영역에 드롭 (오른쪽에서 추가, insertIndex 0)
  const handleDropToCategoryGroup = (e: React.DragEvent) => {
    e.preventDefault();
    setDropZoneActive(false);
    setDropInsertIndex(null);
    if (draggedIndex === null && draggedVoucher) {
      handleDropFromRight(0, e);
    }
  };

  // 삽입 슬롭에 드롭 (오른쪽에서 추가 or 가운데 내부 순서 변경)
  const handleDropOnSlot = (insertIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    setDropInsertIndex(null);
    if (draggedIndex === null) {
      handleDropFromRight(insertIndex, e);
    } else {
      handleDropReorder(insertIndex)(e);
    }
  };

  // 가운데 영역에서 순서 변경 — 슬롭(삽입 위치)에 드롭
  const handleDropReorder = (slotIndex: number) => async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropInsertIndex(null);

    if (!selectedCategoryGroupCode || draggedIndex === null || !projectDetail) return;
    if (slotIndex === draggedIndex) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode || !draggedVoucher) return;

    const treeCode = getTreeCode();
    if (!treeCode) return;

    const currentVouchers = [...(categoryGroupVouchers.get(selectedCategoryGroupCode) || [])];
    const [removed] = currentVouchers.splice(draggedIndex, 1);
    const insertIndex = slotIndex > draggedIndex ? slotIndex - 1 : slotIndex;
    currentVouchers.splice(insertIndex, 0, removed);

    const prev = insertIndex > 0 ? currentVouchers[insertIndex - 1] : null;
    const next = insertIndex < currentVouchers.length - 1 ? currentVouchers[insertIndex + 1] : null;
    const sortOrder = generateBetween(prev?.sortOrder ?? null, next?.sortOrder ?? null);

    try {
      setApiBusy(true);
      await updateVoucherInTree(projectCode, removed.voucherCode, {
        operation: 'update',
        treeCode,
        sortOrder
      });
      const reordered = currentVouchers.map((cv, index) => ({
        ...cv,
        order: index,
        sortOrder: cv.voucherCode === removed.voucherCode ? sortOrder : cv.sortOrder
      }));
      setCategoryGroupVouchers(prevMap => new Map(prevMap).set(selectedCategoryGroupCode, reordered));
    } catch (err) {
      console.error('순서 변경 실패:', err);
      alert(err instanceof Error ? err.message : '순서 변경에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  // 카테고리/그룹에서 교환권 제거 (즉시 API 호출)
  const handleRemoveVoucher = async (voucherCode: string) => {
    if (!selectedCategoryGroupCode || !projectDetail) return;

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) return;

    const treeCode = getTreeCode();
    if (!treeCode) return;

    try {
      setApiBusy(true);
      await updateVoucherInTree(projectCode, voucherCode, { operation: 'remove', treeCode });
      const currentVouchers = (categoryGroupVouchers.get(selectedCategoryGroupCode) || [])
        .filter(cv => cv.voucherCode !== voucherCode)
        .map((cv, index) => ({ ...cv, order: index }));
      setCategoryGroupVouchers(prev => new Map(prev).set(selectedCategoryGroupCode, currentVouchers));
    } catch (err) {
      console.error('교환권 제거 실패:', err);
      alert(err instanceof Error ? err.message : '교환권 제거에 실패했습니다.');
    } finally {
      setApiBusy(false);
    }
  };

  // 교환권 카드 컴포넌트
  const VoucherCard: React.FC<{
    voucher: Voucher;
    isDraggable?: boolean;
    showRemove?: boolean;
    onRemove?: () => void;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
  }> = ({ voucher, isDraggable = true, showRemove = false, onRemove, onDragStart, onDragEnd }) => {
    return (
      <div
        draggable={isDraggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="group bg-white border-2 border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-md transition-all cursor-move"
      >
        <div className="flex items-center gap-3">
          {isDraggable && (
            <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
          )}
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{voucher.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500 font-mono">{voucher.masterCode}</span>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-medium text-gray-700">{formatPrice(voucher.price)}</span>
            </div>
          </div>
          {showRemove && onRemove && (
            <button
              onClick={onRemove}
              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // 카테고리/그룹 트리 아이템 렌더링
  const renderCategoryGroupTreeItem = (node: CategoryGroupNode, level: number = 0): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedCodes.has(node.code);
    const isSelected = selectedCategoryGroupCode === node.code;

    return (
      <div key={node.code}>
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            isSelected ? 'bg-indigo-100 text-indigo-900 font-medium' : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
          onClick={() => handleCategoryGroupSelect(node.code)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryGroupToggle(node.code);
              }}
              className="p-0.5"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          {!hasChildren && <div className="w-5" />}
          <FolderTree className="w-4 h-4" />
          <span className="text-sm">{node.title}</span>
          {node.code && (
            <span className="text-xs text-gray-400">{node.code}</span>
          )}
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            교환권: {!treeCountsLoaded ? '-' : (treeNodeVoucherCounts[`${categoryGroupCode}:${node.code}`] ?? 0)}
          </span>
          {node.type && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
              {node.type === 'CATEGORY' ? '카테고리' : '그룹'}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderCategoryGroupTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // 가격 포맷팅
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  // 현재 카테고리/그룹의 교환권 목록 (sortOrder 기준 정렬)
  const currentCategoryGroupVouchers = selectedCategoryGroupCode
    ? (categoryGroupVouchers.get(selectedCategoryGroupCode) || [])
        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
        .map(cv => allVouchers.find(v => v.code === cv.voucherCode))
        .filter((v): v is Voucher => v !== undefined)
    : [];

  // 프로젝트 정보가 없을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="카테고리/그룹 교환권 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* 컨텐츠 영역 */}
        <main className="flex-1 flex flex-col min-h-0 p-4 sm:p-6">
          {/* 카테고리/그룹 정보 및 변경사항 관리 영역 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex-shrink-0">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                {selectedCategoryGroup && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      <FolderTree className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedCategoryGroup.title}</h3>
                      <p className="text-xs text-gray-500">{selectedCategoryGroup.brandName ? `${selectedCategoryGroup.brandName} (${selectedCategoryGroup.code})` : selectedCategoryGroup.code}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/vouchers/category-groups')}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>목록으로</span>
                </button>
              </div>
            </div>
            {apiBusy && (
              <div className="mt-3 text-sm text-gray-500 flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                저장 중…
              </div>
            )}
          </div>

          {/* 3단 레이아웃 컨텐츠 영역 */}
          <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
            {/* 왼쪽: 카테고리/그룹 트리 */}
            <div className="col-span-3 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">카테고리/그룹</h3>
                <p className="text-xs text-gray-500 mt-1">
                  하위 카테고리/그룹
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {isLoadingTree ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : treeData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <FolderTree className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">하위 카테고리/그룹이 없습니다.</p>
                  </div>
                ) : (
                  treeData.map((node) => renderCategoryGroupTreeItem(node))
                )}
              </div>
            </div>

            {/* 가운데: 선택된 카테고리/그룹의 교환권 */}
            <div className="col-span-5 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  {selectedCategoryGroupCode
                    ? `카테고리/그룹 교환권 (${currentCategoryGroupVouchers.length})`
                    : '카테고리/그룹을 선택하세요'}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  드래그하여 순서 변경 또는 오른쪽에서 추가
                </p>
              </div>
              <div
                className={`flex-1 overflow-y-auto p-4 space-y-2 ${
                  dropZoneActive ? 'bg-indigo-50 border-4 border-dashed border-indigo-300' : ''
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropZoneActive(true);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains((e.relatedTarget as Node) || null)) {
                    setDropInsertIndex(null);
                    setDropZoneActive(false);
                  }
                }}
                onDrop={handleDropToCategoryGroup}
              >
                {selectedCategoryGroupCode ? (
                  isLoadingNodeVouchers ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  ) : currentCategoryGroupVouchers.length > 0 ? (
                    <div className="flex flex-col gap-0">
                      {currentCategoryGroupVouchers.map((voucher, index) => (
                        <React.Fragment key={voucher.code}>
                          {/* 삽입 슬롭: 맨 위·아이템 사이·맨 아래 */}
                          <div
                            className={`min-h-[12px] flex-shrink-0 rounded transition-colors ${
                              dropInsertIndex === index
                                ? 'bg-indigo-200 border-2 border-indigo-400 border-dashed'
                                : 'hover:bg-indigo-50/50'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDropInsertIndex(index);
                              setDropZoneActive(true);
                            }}
                            onDrop={handleDropOnSlot(index)}
                          />
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDropInsertIndex(index);
                              setDropZoneActive(true);
                            }}
                            onDrop={handleDropOnSlot(index)}
                          >
                            <VoucherCard
                              voucher={voucher}
                              showRemove
                              onRemove={() => handleRemoveVoucher(voucher.code)}
                              onDragStart={handleDragStartFromCategory(voucher, index)}
                              onDragEnd={handleDragEnd}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                      {/* 맨 끝 삽입 슬롭 */}
                      <div
                        className={`min-h-[12px] flex-shrink-0 rounded transition-colors ${
                          dropInsertIndex === currentCategoryGroupVouchers.length
                            ? 'bg-indigo-200 border-2 border-indigo-400 border-dashed'
                            : 'hover:bg-indigo-50/50'
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDropInsertIndex(currentCategoryGroupVouchers.length);
                          setDropZoneActive(true);
                        }}
                        onDrop={handleDropOnSlot(currentCategoryGroupVouchers.length)}
                      />
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center py-20"
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDropZoneActive(true);
                      }}
                      onDragLeave={() => setDropZoneActive(false)}
                      onDrop={handleDropToCategoryGroup}
                    >
                      <Package className="w-16 h-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 text-center px-4">
                        오른쪽에서 교환권을 드래그하여 추가하세요
                      </p>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center py-20">
                    <FolderTree className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">카테고리/그룹을 선택하세요</p>
                  </div>
                )}
              </div>
            </div>

            {/* 오른쪽: 전체 교환권 */}
            <div className="col-span-4 bg-white rounded-lg border-2 border-gray-200 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  전체 교환권 ({allVouchers.length})
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  드래그하여 카테고리/그룹에 추가
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {isLoadingVouchers ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : voucherListError ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-red-600 text-sm mb-4 text-center">{voucherListError}</p>
                    <button
                      onClick={() => {
                        setVoucherListError(null);
                        // 다시 로드
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                ) : allVouchers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Package className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">
                      교환권이 없습니다.
                    </p>
                  </div>
                ) : (
                  allVouchers.map((voucher) => (
                    <VoucherCard
                      key={voucher.code}
                      voucher={voucher}
                      onDragStart={handleDragStartFromAll(voucher)}
                      onDragEnd={handleDragEnd}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CategoryGroupVoucherConfig;
